import { useCallback, useState } from 'react';
import { identifyUser } from '../../lib/identifyUser';
import { authenticate, AuthErrorMessage, AuthPayload, AuthAction } from '../../lib/authenticate';
import { useCookies } from '../useCookies';
import { useUser } from '../useUser';
import { RECAPTCHA_ACTIONS, useRecaptcha } from '../useRecaptcha';
import { useSessionQueryParams } from '../useSessionQueryParams';

type HandleAuthProps =
  | {
      guest?: false;
      email: string;
      first_name?: string;
      last_name?: string;
      password: string;
      remember?: boolean;
      opted_in?: boolean;
    }
  | { guest: true; email: string; remember: boolean; opted_in?: boolean };

export const useAuth = (action: AuthAction) => {
  const [error, setError] = useState<AuthErrorMessage>('');
  const [loading, setLoading] = useState(false);
  const { handleRecaptchaV2, handleRecaptchaV3 } = useRecaptcha();
  const { setUserData } = useUser();
  const {
    cookies: { cartId }
  } = useCookies();
  const { bypass_rack_attack: bypassRackAttack } = useSessionQueryParams();

  const handleAuth = useCallback(
    ({ remember, ...values }: HandleAuthProps) => {
      const handleError = (error: Error) => {
        setError(error.message);
        setLoading(false);
        throw error;
      };

      const handleSuccess = (user: AuthPayload) => {
        setUserData({ remember, ...user });
        setLoading(false);
        identifyUser(user);
        return user;
      };

      setLoading(true);

      const attributes = { ...values, cart_id: cartId };

      if (bypassRackAttack) {
        return authenticate({ action, attributes, bypassRackAttack: bypassRackAttack as string })
          .then(handleSuccess)
          .catch(handleError);
      }

      return handleRecaptchaV3(RECAPTCHA_ACTIONS[action]).then((tokenV3: string) => {
        return authenticate({
          action,
          attributes: {
            recaptcha: {
              token: tokenV3,
              type: 'v3'
            },
            ...attributes
          }
        })
          .then(handleSuccess)
          .catch(error => {
            // If recaptcha V3 fails, give the user a chance with V2
            if (error.code === 'recaptcha_failed') {
              return handleRecaptchaV2().then((tokenV2: string) => {
                return authenticate({
                  action,
                  attributes: {
                    recaptcha: {
                      token: tokenV2,
                      type: 'v2'
                    },
                    ...attributes
                  }
                })
                  .then(handleSuccess)
                  .catch(handleError);
              });
            } else {
              handleError(error);
            }
          });
      });
    },
    [cartId, bypassRackAttack, handleRecaptchaV3, action, setUserData, handleRecaptchaV2]
  );

  return { error, loading, handleAuth };
};
